import type { ReactNode } from 'react';
import { cn } from './ui/utils';
import SurfaceCard from './SurfaceCard';
import { getPriorityColors, getStatusColors, getTaskTypeColors } from '../constants/colorScheme';

type SummaryStatCardColor = 'blue' | 'green' | 'purple' | 'orange';
type ProgressStatCardColor = 'green' | 'blue' | 'gray' | 'red';

interface SummaryStatCardProps {
  icon: ReactNode;
  label: string;
  value: string;
  color: SummaryStatCardColor;
  className?: string;
}

interface ProgressStatCardProps {
  label: string;
  value: number;
  total: number;
  color: ProgressStatCardColor;
  icon: ReactNode;
  className?: string;
}

const summaryColorClasses: Record<SummaryStatCardColor, string> = {
  blue: getStatusColors('Active').badge,
  green: getStatusColors('Completed').badge,
  purple: getTaskTypeColors('Improvement').badge,
  orange: getPriorityColors('High').badge,
};

const progressColorClasses: Record<ProgressStatCardColor, string> = {
  green: `${getStatusColors('Completed').container} ${getStatusColors('Completed').text}`,
  blue: `${getStatusColors('Active').container} ${getStatusColors('Active').text}`,
  gray: `${getStatusColors('Not Started').container} ${getStatusColors('Not Started').text}`,
  red: `${getStatusColors('Blocked').container} ${getStatusColors('Blocked').text}`,
};

export function SummaryStatCard({ icon, label, value, color, className }: SummaryStatCardProps) {
  // Horizontal layout: small icon on the left, value + label stacked tightly on the right.
  // Compact padding on mobile (`p-3`) opens up to a roomier feel from `sm:` breakpoint.
  return (
    <SurfaceCard className={cn('p-3 sm:p-4', className)}>
      <div className="flex items-center gap-3">
        <div
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border sm:h-10 sm:w-10',
            summaryColorClasses[color],
          )}
        >
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-lg font-semibold leading-tight text-foreground sm:text-xl">
            {value}
          </div>
          <div className="truncate text-xs text-muted-foreground sm:text-sm">{label}</div>
        </div>
      </div>
    </SurfaceCard>
  );
}

export function ProgressStatCard({ label, value, total, color, icon, className }: ProgressStatCardProps) {
  const percent = total > 0 ? (value / total) * 100 : 0;

  // Horizontal layout matching SummaryStatCard: icon left, value/label/percent stacked right.
  return (
    <div className={cn('rounded-lg border p-3', progressColorClasses[color], className)}>
      <div className="flex items-center gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center opacity-80">{icon}</div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-1.5">
            <span className="text-lg font-semibold leading-none sm:text-xl">{value}</span>
            <span className="text-xs font-normal opacity-75">/ {total}</span>
            <span className="ml-auto text-xs opacity-75">{percent.toFixed(0)}%</span>
          </div>
          <div className="mt-0.5 truncate text-xs opacity-80">{label}</div>
        </div>
      </div>
    </div>
  );
}